import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable"
import TooltipOption from "../../../core/common/tooltipOption"
import { all_routes } from "../../router/all_routes"
import { toast } from "react-toastify"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"
import { getAllContractors } from "../../../service/contractor"
import {
  listContractorPayoutPayments,
  createContractorPayoutPayment,
  cancelContractorPayoutPayment,
  getContractorPayoutSummary,
  getAggregatedPayoutSummary,
  ContractorPayoutPayment,
  ContractorPayoutSummary,
} from "../../../service/contractorPayoutPayment"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel, absoluteCampusLogoUrl } from "../../../utils/campusLogos"
import Swal from "sweetalert2"

const TransportContractorPayment = () => {
  const routesDef = all_routes
  const payoutPageRootRef = useRef<HTMLDivElement>(null)
  const currentSession = useSelector(selectCurrentSession)
  const currentBranch = useSelector(selectCurrentBranch)

  // Contractor filter
  const [contractorOptions, setContractorOptions] = useState<Option[]>([])
  const [selectedContractorId, setSelectedContractorId] = useState<string>("")

  const ALL_OPTION: Option = { value: "", label: "All" }
  const allContractorOptions = [ALL_OPTION, ...contractorOptions]

  // Summary cards
  const [summary, setSummary] = useState<ContractorPayoutSummary | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)

  // Add Payment modal
  const [modalOpen, setModalOpen] = useState(false)
  const [modalContractorId, setModalContractorId] = useState<string>("")
  const [payAmount, setPayAmount] = useState<string>("")
  const [payNote, setPayNote] = useState<string>("")
  const [payDate, setPayDate] = useState<string>(new Date().toISOString().slice(0, 10))
  const [paying, setPaying] = useState(false)

  // Payment history
  const [data, setData] = useState<ContractorPayoutPayment[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20, total: 0 })
  const [searchText, setSearchText] = useState<string>("")
  const [totalCount, setTotalCount] = useState(0)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)

  // Per-row receipt print
  const [selectedPaymentForPrint, setSelectedPaymentForPrint] = useState<ContractorPayoutPayment | null>(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const paymentPrintRef = useRef<HTMLDivElement>(null)

  // Session-derived dates (recomputed on every render — always in sync with Redux)
  const sessionStartDate = currentSession?.startDate
    ? new Date(currentSession.startDate).toISOString().slice(0, 10)
    : ""
  const sessionEndDate = currentSession?.endDate
    ? new Date(currentSession.endDate).toISOString().slice(0, 10)
    : ""

  // Up To Date filter
  const [upToDate, setUpToDate] = useState<string>(
    currentSession?.endDate
      ? new Date(currentSession.endDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  )

  // ── Fix date picker open on click ────────────────────────────────────────
  useEffect(() => {
    const root = payoutPageRootRef.current
    if (!root) return
    const onClickCapture = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof HTMLInputElement)) return
      if (!root.contains(t)) return
      if (t.type !== "date" || t.disabled || t.readOnly) return
      const el = t as HTMLInputElement & { showPicker?: () => void }
      if (typeof el.showPicker !== "function") return
      try { el.showPicker() } catch { /* not supported */ }
    }
    document.addEventListener("click", onClickCapture, true)
    return () => document.removeEventListener("click", onClickCapture, true)
  }, [])

  // ── Fetch helpers (explicit params — never close over stale state) ────────

  const fetchSummary = async (startDate: string, endDate: string, contractorId?: string) => {
    if (!startDate || !endDate) { setSummary(null); return }
    setLoadingSummary(true)
    setSummary(null)
    try {
      const res = contractorId
        ? await getContractorPayoutSummary({ contractorId, startDate, endDate })
        : await getAggregatedPayoutSummary({ startDate, endDate })
      setSummary(res)
    } catch {
      setSummary(null)
    } finally {
      setLoadingSummary(false)
    }
  }

  const fetchHistory = async (opts: {
    startDate: string
    endDate: string
    contractorId?: string
    search?: string
    page: number
    limit: number
  }) => {
    setHistoryLoading(true)
    try {
      const res = await listContractorPayoutPayments({
        contractorId: opts.contractorId,
        startDate: opts.startDate,
        endDate: opts.endDate,
        search: opts.search || undefined,
        page: opts.page,
        limit: opts.limit,
      })
      const list = Array.isArray(res?.data) ? res.data : []
      setData(list.map((p: any) => ({ ...p, key: p._id })))
      const total = res?.pagination?.total ?? 0
      setPagination(prev => ({ ...prev, total }))
      setTotalCount(total)
    } catch {
      setData([])
    } finally {
      setHistoryLoading(false)
    }
  }

  // ── Load contractors once on mount ───────────────────────────────────────

  useEffect(() => {
    const loadContractors = async () => {
      try {
        const res = await getAllContractors({ page: 1, limit: 500, status: "active" })
        const list = (res?.data ?? []).map((c: any) => ({
          value: c._id || c.id,
          label: c.name || c.contractorId || c._id,
        }))
        setContractorOptions(list)
      } catch {
        setContractorOptions([])
      }
    }
    loadContractors()
  }, [])

  // ── Session change: reset state and reload everything ────────────────────
  // Passes dates directly to fetch functions — no risk of reading stale state.

  useEffect(() => {
    if (!sessionStartDate) return
    const newEnd = sessionEndDate || sessionStartDate
    setUpToDate(newEnd)
    setPagination({ currentPage: 1, limit: 20, total: 0 })
    setSearchText("")
    setPayDate(prev => {
      const today = new Date().toISOString().slice(0, 10)
      let d = prev || today
      if (d < sessionStartDate) d = sessionStartDate
      if (sessionEndDate && d > sessionEndDate) d = sessionEndDate
      return d
    })
    fetchSummary(sessionStartDate, newEnd, selectedContractorId || undefined)
    fetchHistory({
      startDate: sessionStartDate,
      endDate: newEnd,
      contractorId: selectedContractorId || undefined,
      search: "",
      page: 1,
      limit: 20,
    })
  }, [sessionStartDate, sessionEndDate]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Up To Date change handler ────────────────────────────────────────────

  const handleUpToDateChange = (val: string) => {
    if (!val) return
    let clamped = val
    if (sessionStartDate && val < sessionStartDate) clamped = sessionStartDate
    if (sessionEndDate && val > sessionEndDate) clamped = sessionEndDate
    setUpToDate(clamped)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    fetchSummary(sessionStartDate, clamped, selectedContractorId || undefined)
    fetchHistory({
      startDate: sessionStartDate,
      endDate: clamped,
      contractorId: selectedContractorId || undefined,
      search: searchText,
      page: 1,
      limit: pagination.limit,
    })
  }

  // ── Contractor filter change handler ─────────────────────────────────────

  const handleContractorChange = (opt: Option | null) => {
    const id = (opt?.value as string) ?? ""
    setSelectedContractorId(id)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    if (sessionStartDate && upToDate) {
      fetchSummary(sessionStartDate, upToDate, id || undefined)
      fetchHistory({
        startDate: sessionStartDate,
        endDate: upToDate,
        contractorId: id || undefined,
        search: searchText,
        page: 1,
        limit: pagination.limit,
      })
    }
  }

  // ── Export / Print helpers ───────────────────────────────────────────────

  const EXPORT_HEADERS = ["Contractor", "Date", "Amount (RS)", "Note", "Status"]

  const toExportRows = (payments: ContractorPayoutPayment[]): string[][] =>
    payments.map(p => [
      p.contractor?.name || "—",
      p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : "—",
      p.amount != null ? p.amount.toLocaleString() : "—",
      p.note || "—",
      p.status === "cancelled" ? "Cancelled" : "Paid",
    ])

  const fetchAllForPrint = async (): Promise<ContractorPayoutPayment[]> => {
    const res = await listContractorPayoutPayments({
      contractorId: selectedContractorId || undefined,
      startDate: sessionStartDate || undefined,
      endDate: upToDate || undefined,
      limit: totalCount || 500,
      page: 1,
    })
    return Array.isArray(res?.data) ? res.data : []
  }

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No payments to print."); return }
    setPrintLoading(true)
    try {
      const all = await fetchAllForPrint()
      const contractorLabel = selectedContractorId
        ? contractorOptions.find(o => o.value === selectedContractorId)?.label ?? "Contractor"
        : "All Contractors"
      const title = `Contractor Payout — ${contractorLabel}`
      const dateRange = `Period: ${sessionStartDate} to ${upToDate}`

      const schoolEarning = (summary?.totalCollected ?? 0) - (summary?.amountToPay ?? 0)
      const metrics = [
        { label: "Total Collected", value: formatCurrency(summary?.totalCollected) },
        { label: "School Earning", value: formatCurrency(schoolEarning) },
        { label: "Amount to Pay", value: formatCurrency(summary?.amountToPay) },
        { label: "Already Paid", value: formatCurrency(summary?.alreadyPaid) },
        { label: "Balance", value: formatCurrency(summary?.balance) },
      ]

      const metricCells = metrics
        .map(m => `<td><div class="metric-label">${m.label}</div><div class="metric-value">${m.value}</div></td>`)
        .join("")

      const headerRow = EXPORT_HEADERS.map(h => `<th>${h}</th>`).join("")
      const bodyRows = toExportRows(all)
        .map(row => `<tr>${row.map(cell => `<td>${cell ?? ""}</td>`).join("")}</tr>`)
        .join("")

      const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      const brandName = campusBrandName(currentBranch)
      const branchLabel = campusBranchLabel(currentBranch)
      // Absolute — this HTML is written into a brand-new about:blank window,
      // so a relative path (e.g. the local static-file logo fallback) would
      // have no base URL to resolve against and silently fail.
      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)

      const win = window.open("", "_blank")
      if (!win) return
      win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #111; }
      .brand { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
      .brand img { height: 32px; object-fit: contain; }
      .brand strong { font-size: 14px; }
      h2 { font-size: 15px; margin-bottom: 4px; }
      .date-range { font-size: 10px; color: #555; margin-bottom: 14px; }
      .metrics { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
      .metrics td { border: 1px solid #ddd; padding: 8px 12px; text-align: center; width: 20%; }
      .metric-label { font-size: 9px; color: #666; margin-bottom: 3px; }
      .metric-value { font-size: 13px; font-weight: bold; color: #222; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
      th { background: #f0f0f0; font-weight: bold; }
      tr:nth-child(even) { background: #fafafa; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>
    <div class="brand">
      <img src="${escapeHtml(logoUrl)}" onerror="this.style.display='none'" />
      <strong>${escapeHtml(brandName)}${branchLabel ? ` - ${escapeHtml(branchLabel)}` : ""}</strong>
    </div>
    <h2>${title}</h2>
    <div class="date-range">${dateRange}</div>
    <table class="metrics"><tr>${metricCells}</tr></table>
    <table>
      <thead><tr>${headerRow}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </body>
</html>`)
      win.document.close()
      win.focus()
      win.print()
      win.close()
    } catch {
      toast.error("Failed to prepare print data.")
    } finally {
      setPrintLoading(false)
    }
  }

  const getExportTitle = () => {
    const contractorLabel = selectedContractorId
      ? contractorOptions.find(o => o.value === selectedContractorId)?.label ?? "Contractor"
      : "All Contractors"
    return `Contractor Payout — ${contractorLabel} (${sessionStartDate} to ${upToDate})`
  }

  const getMetrics = () => {
    const schoolEarning = (summary?.totalCollected ?? 0) - (summary?.amountToPay ?? 0)
    return [
      { label: "Total Collected", value: formatCurrency(summary?.totalCollected) },
      { label: "School Earning",  value: formatCurrency(schoolEarning) },
      { label: "Amount to Pay",   value: formatCurrency(summary?.amountToPay) },
      { label: "Already Paid",    value: formatCurrency(summary?.alreadyPaid) },
      { label: "Balance",         value: formatCurrency(summary?.balance) },
    ]
  }

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No payments to export."); return }
    setPdfLoading(true)
    try {
      const all = await fetchAllForPrint()
      const title = getExportTitle()
      const metrics = getMetrics()

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })

      doc.setFontSize(12)
      doc.text(title, 14, 12)

      autoTable(doc, {
        head: [metrics.map(m => m.label)],
        body: [metrics.map(m => m.value)],
        startY: 18,
        styles: { fontSize: 9, cellPadding: 3, halign: "center" },
        headStyles: { fillColor: [60, 90, 153], textColor: 255, fontStyle: "bold" },
        bodyStyles: { fontStyle: "bold" },
      })

      const afterMetrics = (doc as any).lastAutoTable.finalY + 6
      doc.setFontSize(10)
      doc.text("Payment History", 14, afterMetrics)
      autoTable(doc, {
        head: [EXPORT_HEADERS],
        body: toExportRows(all),
        startY: afterMetrics + 4,
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      })

      doc.save("contractor-payout.pdf")
    } catch {
      toast.error("Failed to export PDF.")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No payments to export."); return }
    setExcelLoading(true)
    try {
      const all = await fetchAllForPrint()
      const metrics = getMetrics()

      const sheetData = [
        metrics.map(m => m.label),
        metrics.map(m => m.value),
        [],
        EXPORT_HEADERS,
        ...toExportRows(all),
      ]

      const ws = XLSX.utils.aoa_to_sheet(sheetData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Contractor Payout")
      XLSX.writeFile(wb, "contractor-payout.xlsx")
    } catch {
      toast.error("Failed to export Excel.")
    } finally {
      setExcelLoading(false)
    }
  }

  // ── Modal handlers ───────────────────────────────────────────────────────

  const openModal = () => {
    setModalContractorId(selectedContractorId)
    setPayAmount("")
    setPayNote("")
    const today = new Date().toISOString().slice(0, 10)
    let defaultDate = today
    if (sessionStartDate && defaultDate < sessionStartDate) defaultDate = sessionStartDate
    if (sessionEndDate && defaultDate > sessionEndDate) defaultDate = sessionEndDate
    setPayDate(defaultDate)
    setModalOpen(true)
  }

  const closeModal = () => {
    if (paying) return
    setModalOpen(false)
  }

  const handlePay = async () => {
    if (!modalContractorId) {
      toast.error("Please select a contractor.")
      return
    }
    if (!payDate) {
      toast.error("Please select a payment date.")
      return
    }
    const amt = Number(payAmount)
    if (isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid amount greater than 0.")
      return
    }
    setPaying(true)
    try {
      await createContractorPayoutPayment({
        contractorId: modalContractorId,
        paymentDate: payDate,
        amount: amt,
        note: payNote.trim() || undefined,
      })
      toast.success("Payment recorded successfully.")
      setModalOpen(false)
      await Promise.all([
        fetchSummary(sessionStartDate, upToDate, selectedContractorId || undefined),
        fetchHistory({ startDate: sessionStartDate, endDate: upToDate, contractorId: selectedContractorId || undefined, search: searchText, page: pagination.currentPage, limit: pagination.limit }),
      ])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to record payment")
    } finally {
      setPaying(false)
    }
  }

  const handleCancel = async (payment: ContractorPayoutPayment) => {
    const result = await Swal.fire({
      title: "Cancel Payment?",
      text: `Cancel Rs ${payment.amount?.toLocaleString()} payment? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel It",
      cancelButtonText: "No",
    })
    if (!result.isConfirmed) return
    try {
      await cancelContractorPayoutPayment(payment._id)
      toast.success("Payment cancelled.")
      await Promise.all([
        fetchSummary(sessionStartDate, upToDate, selectedContractorId || undefined),
        fetchHistory({ startDate: sessionStartDate, endDate: upToDate, contractorId: selectedContractorId || undefined, search: searchText, page: pagination.currentPage, limit: pagination.limit }),
      ])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to cancel payment")
    }
  }

  const handlePrintPayment = (record: ContractorPayoutPayment) => {
    setSelectedPaymentForPrint(record)
    setShowPrintModal(true)
  }

  const handleClosePrintModal = () => {
    setShowPrintModal(false)
    setSelectedPaymentForPrint(null)
  }

  const handlePrintPaymentReceipt = () => {
    const content = paymentPrintRef.current
    if (!content) return
    const payment = selectedPaymentForPrint
    const docTitle = `Contractor-Payment-${payment?.contractor?.name || ""}-${payment?.paymentDate || ""}`
    const win = window.open("", "_blank")
    if (!win) return
    win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${docTitle}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #000; }
      .printable-main { max-width: 400px; margin: 0 auto; padding: 10px; }
      .date { margin: 0; font-size: 16px; font-weight: bold; }
      .heading { font-size: 22px; letter-spacing: 1px; color: #2c3e50; }
      .branch-paragraph { font-size: 16px; font-weight: bold; color: #2c3e50; margin: 0; }
      .class { margin: 0; font-size: 15px; }
      table { width: 100%; border-collapse: collapse; }
      .table-main { font-size: 16px; width: 100%; border-bottom: 1px solid #000; }
      .table-item { font-size: 16px; width: 100%; border-bottom: 1px solid #000; padding: 4px; }
      .table-item-right { padding: 8px; border-bottom: 1px solid #000; text-align: right; font-size: 16px; }
      .total-left { padding: 8px; font-weight: bold; text-align: left; font-size: 16px; border: none; }
      .total-right { padding: 8px; font-weight: bold; text-align: right; font-size: 16px; border: none; }
      .note { font-size: 14px; margin: 0; text-align: center; }
      .text-center { text-align: center; }
      .text-end { text-align: right; }
      .text-uppercase { text-transform: uppercase; }
      .fw-bold { font-weight: bold; }
      .mb-1 { margin-bottom: 4px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-3 { margin-bottom: 12px; }
      .mt-2 { margin-top: 8px; }
      .mt-3 { margin-top: 12px; }
      .row { display: flex; }
      .col-6 { flex: 0 0 50%; max-width: 50%; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>${content.innerHTML}</body>
</html>`)
    win.document.close()
    win.focus()
    win.print()
    win.close()
    handleClosePrintModal()
  }

  const formatCurrency = (v?: number) =>
    v != null ? `Rs ${v.toLocaleString()}` : "Rs 0"

  // ── Table columns ────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Contractor",
      dataIndex: "contractor",
      render: (v: any) => v?.name || "—",
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : "—"),
      sorter: (a: ContractorPayoutPayment, b: ContractorPayoutPayment) =>
        new Date(a.paymentDate || 0).getTime() - new Date(b.paymentDate || 0).getTime(),
    },
    {
      title: "Amount (RS)",
      dataIndex: "amount",
      render: (v: number, record: ContractorPayoutPayment) => (
        <span style={{ textDecoration: record.status === "cancelled" ? "line-through" : "none", color: record.status === "cancelled" ? "#999" : undefined }}>
          {v != null ? v.toLocaleString() : "—"}
        </span>
      ),
      align: "right" as const,
    },
    {
      title: "Note",
      dataIndex: "note",
      render: (v: string) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: string) =>
        v === "cancelled" ? (
          <span className="badge badge-soft-danger">Cancelled</span>
        ) : (
          <span className="badge badge-soft-success">Paid</span>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: ContractorPayoutPayment) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handlePrintPayment(record)}
          >
            Print
          </button>
          {record.status !== "cancelled" && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleCancel(record)}
            >
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ]

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="transport-contractor-payout-page" ref={payoutPageRootRef}>
      <div className="page-wrapper">
        <div className="content">

          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Contractor Payout</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routesDef.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Contractor Payout
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center gap-2 flex-wrap">
              <TooltipOption
                onRefresh={async () => {
                  await Promise.all([
                    fetchSummary(sessionStartDate, upToDate, selectedContractorId || undefined),
                    fetchHistory({ startDate: sessionStartDate, endDate: upToDate, contractorId: selectedContractorId || undefined, search: searchText, page: pagination.currentPage, limit: pagination.limit }),
                  ])
                }}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <button className="btn btn-primary" onClick={openModal}>
                  <i className="ti ti-plus me-1" />
                  Add Payment
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="d-flex justify-content-end align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 fw-semibold text-nowrap">Contractor</label>
                  <div style={{ width: 220 }}>
                    <CommonSelect
                      className="select"
                      options={allContractorOptions}
                      value={allContractorOptions.find(o => o.value === selectedContractorId) ?? ALL_OPTION}
                      onChange={handleContractorChange}
                      placeholder="All"
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 fw-semibold text-nowrap">Up To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ width: 160 }}
                    value={upToDate}
                    min={sessionStartDate || undefined}
                    max={sessionEndDate || undefined}
                    onChange={e => handleUpToDateChange(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row g-3 mb-3">
            {[
              { label: "Total Collected", value: summary?.totalCollected, color: "primary" },
              { label: "School Earning", value: (summary?.totalCollected ?? 0) - (summary?.amountToPay ?? 0), color: "info" },
              { label: "Amount to Pay", value: summary?.amountToPay, color: "warning" },
              { label: "Already Paid", value: summary?.alreadyPaid, color: "success" },
              { label: "Balance", value: summary?.balance, color: summary?.balance && summary.balance > 0 ? "danger" : "secondary" },
            ].map(card => (
              <div key={card.label} className="col">
                <div className={`card border-0 shadow-sm bg-${card.color}-subtle`}>
                  <div className="card-body text-center py-3">
                    {loadingSummary ? (
                      <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                    ) : (
                      <h4 className={`text-${card.color} fw-bold mb-1`}>
                        {formatCurrency(card.value)}
                      </h4>
                    )}
                    <p className="mb-0 text-muted small">{card.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Payment History */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Payment History</h5>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                dataSource={data}
                columns={columns}
                loading={historyLoading}
                setSearchText={(val) => {
                  setSearchText(val)
                  setPagination(prev => ({ ...prev, currentPage: 1 }))
                  fetchHistory({
                    startDate: sessionStartDate,
                    endDate: upToDate,
                    contractorId: selectedContractorId || undefined,
                    search: val,
                    page: 1,
                    limit: pagination.limit,
                  })
                }}
                pagination={{
                  currentPage: pagination.currentPage,
                  limit: pagination.limit,
                  total: pagination.total,
                  onChange: (page, pageSize) => {
                    const limit = pageSize ?? pagination.limit
                    setPagination(prev => ({ ...prev, currentPage: page, limit }))
                    fetchHistory({
                      startDate: sessionStartDate,
                      endDate: upToDate,
                      contractorId: selectedContractorId || undefined,
                      search: searchText,
                      page,
                      limit,
                    })
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Per-row Payment Receipt Modal */}
      {showPrintModal && selectedPaymentForPrint && (
        <div className="modal fade show main">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Contractor Payment Receipt</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={handleClosePrintModal}
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              <div className="modal-body">
                <div ref={paymentPrintRef}>
                  <div className="invoice-print printable-content printable-main">
                    {/* Date row */}
                    <div className="row mb-3">
                      <div className="col-6">
                        <p className="date">
                          Date:{" "}
                          {selectedPaymentForPrint.paymentDate
                            ? new Date(selectedPaymentForPrint.paymentDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "—"}
                        </p>
                      </div>
                      <div className="col-6 text-end">
                        <p className="date">
                          Status:{" "}
                          {selectedPaymentForPrint.status === "cancelled"
                            ? "Cancelled"
                            : "Paid"}
                        </p>
                      </div>
                    </div>

                    {/* School header */}
                    <div className="text-center mb-2">
                      <img
                        src={absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)}
                        alt={campusBrandName(currentBranch)}
                        style={{ width: "70px", height: "70px", objectFit: "contain" }}
                        className="mb-1"
                      />
                      <h4 className="text-uppercase fw-bold mb-1 heading">
                        {campusBrandName(currentBranch).toUpperCase()}
                      </h4>
                      {campusBranchLabel(currentBranch) ? (
                        <p className="branch-paragraph">{campusBranchLabel(currentBranch).toUpperCase()}</p>
                      ) : null}
                    </div>

                    {/* Contractor info */}
                    <div className="mb-3 text-center">
                      <p className="date">
                        {(selectedPaymentForPrint.contractor?.name || "—").toUpperCase()}
                      </p>
                      <p className="class">Contractor Payment</p>
                    </div>

                    {/* Table */}
                    <table className="table mb-2">
                      <thead className="table-main">
                        <tr>
                          <th className="table-item">Details</th>
                          <th className="table-item">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="table-item">
                            Contractor Payment
                            {selectedPaymentForPrint.note
                              ? ` — ${selectedPaymentForPrint.note}`
                              : ""}
                          </td>
                          <td className="table-item-right">
                            {selectedPaymentForPrint.amount != null
                              ? `${selectedPaymentForPrint.amount.toLocaleString()} /=`
                              : "—"}
                          </td>
                        </tr>

                        <tr>
                          <td colSpan={1} className="total-left">Total:</td>
                          <td className="total-right">
                            {selectedPaymentForPrint.amount != null
                              ? `${selectedPaymentForPrint.amount.toLocaleString()} /=`
                              : "—"}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Generated by */}
                    {selectedPaymentForPrint.createdBy && (
                      <div className="mt-2 text-center">
                        <p className="note">
                          <strong>
                            Generated By:{" "}
                            {`${selectedPaymentForPrint.createdBy.firstName || ""} ${selectedPaymentForPrint.createdBy.lastName || ""}`.trim()}
                          </strong>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2" onClick={handleClosePrintModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handlePrintPaymentReceipt}>
                  <i className="ti ti-printer me-2" />
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {modalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Payment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    disabled={paying}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      Contractor <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className="select"
                      options={contractorOptions}
                      value={contractorOptions.find(o => o.value === modalContractorId) ?? null}
                      onChange={opt => setModalContractorId(opt?.value as string ?? "")}
                      placeholder="Select contractor"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Amount (RS) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0"
                      min={0}
                      step={0.01}
                      value={payAmount}
                      onChange={e => setPayAmount(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={payDate}
                      min={sessionStartDate || undefined}
                      max={sessionEndDate || undefined}
                      onChange={e => {
                        const val = e.target.value
                        if (!val) {
                          const today = new Date().toISOString().slice(0, 10)
                          let d = today
                          if (sessionStartDate && d < sessionStartDate) d = sessionStartDate
                          if (sessionEndDate && d > sessionEndDate) d = sessionEndDate
                          setPayDate(d)
                          return
                        }
                        let clamped = val
                        if (sessionStartDate && val < sessionStartDate) clamped = sessionStartDate
                        if (sessionEndDate && val > sessionEndDate) clamped = sessionEndDate
                        setPayDate(clamped)
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Note</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Optional note"
                      value={payNote}
                      onChange={e => setPayNote(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={paying}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePay}
                    disabled={paying}
                  >
                    {paying && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                    )}
                    {paying ? "Saving..." : "Save Payment"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={closeModal} />
        </>
      )}
    </div>
  )
}

export default TransportContractorPayment
